import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, AlertTriangle, Circle, ExternalLink, RefreshCw, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Admin Onboarding Checklist.
 *
 * Renders the live system-state probe at `/api/admin/onboarding/state`.
 * Nothing is stored — every reload re-computes the truth. Score badge
 * lets the admin see at a glance whether the platform is fully armed
 * for profit generation.
 *
 * The component refreshes on mount and exposes a re-check button so
 * after a config change (e.g. granting Stripe scopes) the admin can
 * see the row flip from amber to green within seconds.
 */
const AdminOnboardingChecklist = ({ dataTestId = 'admin-onboarding' }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetch = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/onboarding/state`);
      setState(r.data);
    } catch (e) {
      // silent — admin already sees other failure signals on the page
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded p-4 text-white/40 flex items-center gap-2 text-sm" data-testid={`${dataTestId}-loading`}>
        <Loader2 className="h-4 w-4 animate-spin" /> Probing platform state…
      </div>
    );
  }
  if (!state) return null;

  const scoreColor =
    state.score >= 90 ? 'text-emerald-300 border-emerald-700/40 bg-emerald-950/40' :
    state.score >= 70 ? 'text-amber-300 border-amber-700/40 bg-amber-950/40' :
    'text-rose-300 border-rose-700/40 bg-rose-950/40';

  const iconFor = (status) => {
    if (status === 'done') return <Check className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />;
    if (status === 'attention') return <AlertTriangle className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />;
    return <Circle className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />;
  };

  return (
    <div className="mb-8" data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#F0A875]">Admin onboarding · live state</p>
          <h2 className="text-white font-['Playfair_Display'] text-xl mt-0.5">Profit-readiness checklist</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded border ${scoreColor}`} data-testid={`${dataTestId}-score`}>
            {state.score}% READY · {state.totals.done}/{state.totals.all}
          </div>
          <button
            onClick={() => fetch(true)}
            disabled={refreshing}
            className="text-white/60 hover:text-white"
            data-testid={`${dataTestId}-refresh`}
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(state.groups).map(([group, items]) => (
          <div key={group} className="bg-white/5 border border-white/10 rounded p-4" data-testid={`${dataTestId}-group-${group.toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50 mb-3">{group}</p>
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.key} className="flex items-start gap-2 text-sm" data-testid={`${dataTestId}-item-${it.key}`}>
                  {iconFor(it.status)}
                  <div className="flex-1 min-w-0">
                    <p className={`leading-tight ${it.status === 'pending' ? 'text-slate-400' : it.status === 'attention' ? 'text-amber-200' : 'text-white'}`}>
                      {it.label}
                    </p>
                    <p className="text-[11px] text-white/40 mt-0.5">{it.detail}</p>
                  </div>
                  {it.fix_url && it.status !== 'done' && (
                    <a
                      href={it.fix_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] uppercase tracking-wider font-bold text-[#F0A875] hover:text-white inline-flex items-center gap-1"
                      data-testid={`${dataTestId}-fix-${it.key}`}
                    >
                      Fix <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-white/30 mt-3 text-right">
        Recomputed every reload · last probe {new Date(state.computed_at).toLocaleTimeString()}
      </p>
    </div>
  );
};

export default AdminOnboardingChecklist;
